import { useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import Loading from "../../shared/components/Loading";
import Empty from "../../shared/components/Empty";
import ErrorMessage from "../../shared/components/Error";
import { AgentMonitorAgent } from "../../../services/AgentMonitorService";
import "./agentMonitor.css";

interface AgentMonitorAgentsTableProps {
  agents: AgentMonitorAgent[];
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
  onReassign: (agent: AgentMonitorAgent) => void;
}

type SortableColumn = "agentName" | "openChats" | "chatsEnded";
type StatusFilter = "all" | "available" | "unavailable";

const AVATAR_COLORS = [
  "#0076b6",
  "#1a7a5e",
  "#8e44ad",
  "#c0392b",
  "#d35400",
  "#16a085",
  "#2c3e50",
  "#7f8c8d"
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const AgentMonitorAgentsTable = ({
  agents,
  loading,
  error,
  onRefresh,
  onReassign
}: AgentMonitorAgentsTableProps) => {
  const { translate } = TranslationService;
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortColumn, setSortColumn] = useState<SortableColumn>("agentName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDir("asc");
    }
  };

  const filteredAgents = agents
    .filter((a) => a.agentName.toLowerCase().includes(searchText.toLowerCase()))
    .filter((a) => {
      if (statusFilter === "all") return true;
      return statusFilter === "available" ? a.available : !a.available;
    })
    .sort((a, b) => {
      let aVal: string | number = a[sortColumn];
      let bVal: string | number = b[sortColumn];
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ column }: { column: SortableColumn }) => {
    if (sortColumn !== column)
      return <i className="far fa-sort am-sort-icon" />;
    return (
      <i
        className={`far fa-sort-${sortDir === "asc" ? "up" : "down"} am-sort-icon`}
        style={{ color: "#1a56db" }}
      />
    );
  };

  return (
    <div className="am-table-card">
      <div className="am-table-toolbar">
        <div className="am-status-filter-group">
          <label
            className="am-status-filter-label"
            htmlFor="agent-status-filter"
          >
            {translate.Status || "Estado"}
          </label>
          <select
            id="agent-status-filter"
            className="am-status-dropdown"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">{translate.All || "Todos"}</option>
            <option value="available">
              {translate.AgentAvailable || "Disponible"}
            </option>
            <option value="unavailable">
              {translate.AgentNotAvailable || "No disponible"}
            </option>
          </select>
        </div>

        <div className="am-search-box">
          <input
            type="text"
            className="am-search-input"
            placeholder={
              translate.SearchAgentByName || "Buscar agente por nombre..."
            }
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <span className="am-search-icon">
            <i className="far fa-search"></i>
          </span>
        </div>
      </div>

      {loading && (
        <div className="p-3">
          <Loading height={200} />
        </div>
      )}
      {!loading && error && (
        <div className="p-3">
          <ErrorMessage
            message={
              translate.ErrorLoadingData || "Error al cargar los agentes"
            }
            onRefresh={onRefresh}
          />
        </div>
      )}
      {!loading && !error && filteredAgents.length === 0 && (
        <div className="p-3">
          <Empty />
        </div>
      )}
      {!loading && !error && filteredAgents.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="am-table">
            <thead>
              <tr>
                <th
                  className={`am-th-sortable${sortColumn === "agentName" ? " am-sorted" : ""}`}
                  onClick={() => handleSort("agentName")}
                >
                  {translate.Agent || "Agente"} <SortIcon column="agentName" />
                </th>
                <th>{translate.Status || "Estado"}</th>
                <th>{translate.TimeInStatus || "Tiempo en estado"}</th>
                <th
                  className={`am-th-sortable${sortColumn === "openChats" ? " am-sorted" : ""}`}
                  style={{ textAlign: "center" }}
                  onClick={() => handleSort("openChats")}
                >
                  {translate.OpenChats || "Chats abiertos"}{" "}
                  <SortIcon column="openChats" />
                </th>
                <th
                  className={`am-th-sortable${sortColumn === "chatsEnded" ? " am-sorted" : ""}`}
                  style={{ textAlign: "center" }}
                  onClick={() => handleSort("chatsEnded")}
                >
                  {translate.FinishedToday || "Finalizadas hoy"}{" "}
                  <SortIcon column="chatsEnded" />
                </th>
                <th>{translate.TableActions || "Acciones"}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => {
                const hasNoOpenChats = agent.openChats === 0;
                return (
                  <tr key={agent.agentId}>
                    <td>
                      <div className="am-agent-cell">
                        <div
                          className="am-agent-avatar"
                          style={{
                            backgroundColor: getAvatarColor(agent.agentName)
                          }}
                        >
                          {agent.agentInitials}
                        </div>
                        <span className="am-agent-name">{agent.agentName}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`am-status-pill ${agent.available ? "available" : "unavailable"}`}
                      >
                        <span
                          className={`am-status-dot ${agent.available ? "available" : "unavailable"}`}
                        />
                        {agent.available
                          ? translate.AgentAvailable || "Disponible"
                          : translate.AgentNotAvailable || "No disponible"}
                      </span>
                    </td>
                    <td className="am-time-cell">
                      {agent.timeInStatus ?? "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span className="am-count am-count-open">
                        {agent.openChats}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span className="am-count am-count-done">
                        {agent.chatsEnded}
                      </span>
                    </td>
                    <td>
                      <button
                        className="am-action-btn"
                        onClick={() => onReassign(agent)}
                        disabled={hasNoOpenChats}
                      >
                        {translate.Reassign || "Reasignar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgentMonitorAgentsTable;
